/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Grid } from '@mui/material';
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  GeometryObject,
} from 'geojson';
import React, { useMemo } from 'react';
import type { RowComponentProps } from 'react-window';
import { List, useListRef } from 'react-window';
import { CustomAccordion, useResizeObserver } from '@opengeoweb/shared';
import { LocationDropdownHeader } from './LocationDropdownHeader';
import type { GeoJsonWithService } from '../types';
import { LocationListItem } from './LocationListItem';

const RowComponent = ({
  index,
  style,
  handleChange,
  selectedLocation,
  features,
  isDisabled = false,
}: RowComponentProps<{
  handleChange: (newSelectedLocation: string) => void;
  selectedLocation: string | undefined;
  features: Feature<GeometryObject, GeoJsonProperties>[];
  isDisabled?: boolean;
}>): React.ReactElement => {
  const feature = features[index];
  return (
    <div style={{ ...style }}>
      <LocationListItem
        handleChange={handleChange}
        isDisabled={isDisabled}
        key={feature.id}
        location={feature.properties?.name as string}
        selectedLocation={selectedLocation ?? ''}
      />
    </div>
  );
};

function useFocusVisible(): {
  focusVisible: boolean;
  onFocus: () => void;
  onBlur: () => void;
  clear: () => void;
} {
  const lastWasKeyboard = React.useRef(false);
  const [focusVisible, setFocusVisible] = React.useState(false);

  React.useEffect(() => {
    const onKey = (): void => {
      lastWasKeyboard.current = true;
    };
    const onPointer = (): void => {
      lastWasKeyboard.current = false;
    };

    window.addEventListener('keydown', onKey, { capture: true });
    window.addEventListener('pointerdown', onPointer, { capture: true });
    window.addEventListener('mousedown', onPointer, { capture: true });
    window.addEventListener('touchstart', onPointer, { capture: true });

    return (): void => {
      window.removeEventListener('keydown', onKey, { capture: true });
      window.removeEventListener('pointerdown', onPointer, {
        capture: true,
      });
      window.removeEventListener('mousedown', onPointer, {
        capture: true,
      });
      window.removeEventListener('touchstart', onPointer, {
        capture: true,
      });
    };
  }, []);

  return {
    focusVisible,
    onFocus: (): void => {
      if (lastWasKeyboard.current) {
        setFocusVisible(true);
      }
    },
    onBlur: () => setFocusVisible(false),
    clear: () => setFocusVisible(false),
  };
}

const FocusableListWrapper = ({
  children,
  onKeyDown,
  'aria-label': ariaLabel,
}: {
  children: React.ReactNode;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  'aria-label': string;
}): React.ReactElement => {
  const fv = useFocusVisible();

  return (
    <div
      aria-label={ariaLabel}
      onBlur={fv.onBlur}
      onFocus={fv.onFocus}
      onKeyDown={onKeyDown}
      onMouseDown={fv.clear}
      role="listbox"
      style={{
        outline: 'none',
        transition: 'box-shadow 120ms ease, background-color 120ms ease',
        ...(fv.focusVisible
          ? {
              boxShadow: '0 0 0 2px var(--focus-ring-color, #1976d2)',
              backgroundColor: 'rgba(25,118,210,0.06)',
            }
          : null),
      }}
      tabIndex={0}
    >
      {children}
    </div>
  );
};

interface FocusableListProps {
  rowCount: number;
  rowHeight: number;
  rowProps: {
    handleChange: (newSelectedLocation: string) => void;
    selectedLocation: string | undefined;
    features: Feature<GeometryObject, GeoJsonProperties>[];
    isDisabled: boolean;
  };
  style: React.CSSProperties;
}

const FocusableList = ({
  rowCount,
  rowHeight,
  rowProps,
  style,
}: FocusableListProps): React.ReactElement => {
  const listRef = useListRef(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const lineHeight = 40;
    const page = 8 * lineHeight;
    const element = listRef.current?.element;
    let next = element?.scrollTop ?? 0;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        next += lineHeight;
        break;
      case 'ArrowUp':
        e.preventDefault();
        next -= lineHeight;
        break;
      case 'PageDown':
        e.preventDefault();
        next += page;
        break;
      case 'PageUp':
        e.preventDefault();
        next -= page;
        break;
      case 'Home':
        e.preventDefault();
        next = 0;
        break;
      default:
        return;
    }

    element?.scrollTo({ top: Math.max(0, next), behavior: 'smooth' });
  };

  return (
    <FocusableListWrapper aria-label="Locations list" onKeyDown={handleKeyDown}>
      <List
        listRef={listRef}
        rowComponent={RowComponent}
        rowCount={rowCount}
        rowHeight={rowHeight}
        rowProps={rowProps}
        style={style}
      />
    </FocusableListWrapper>
  );
};

interface LocationDropdownProps {
  splitGeoJson?: GeoJsonWithService[];
  handleChange: (newSelectedLocation: string) => void;
  selectedLocation: string;
  geoJson?: FeatureCollection<GeometryObject>;
  isDisabled?: boolean;
  defaultExpanded?: boolean;
}

export const LocationDropdown: React.FC<LocationDropdownProps> = ({
  geoJson,
  splitGeoJson,
  handleChange,
  selectedLocation,
  isDisabled = false,
  defaultExpanded,
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');

  const combinedSelectedLocation = geoJson?.features.find((feature) => {
    return selectedLocation === feature.properties?.name;
  });

  const selectedLocationNames = combinedSelectedLocation
    ? Object.keys(combinedSelectedLocation?.properties?.names)
    : [];

  const filteredResults = useMemo(() => {
    return splitGeoJson?.map((dataset) => {
      const filteredFeatures = dataset.geoJson.features.filter((feature) => {
        return feature.properties?.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      });

      return {
        ...dataset,
        geoJson: {
          ...dataset.geoJson,
          features: filteredFeatures,
        },
      };
    });
  }, [searchTerm, splitGeoJson]);

  const listContainerRef = React.useRef<HTMLDivElement>(null);

  const heightObserver = useResizeObserver(
    listContainerRef,
    filteredResults as unknown[],
  );

  return (
    <Box
      data-testid="locationDropdownContainer"
      ref={listContainerRef}
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: 'geowebColors.background.surface',
      }}
    >
      <Grid container>
        <Grid
          container
          size={{ xs: 12 }}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: 'white',
          }}
        >
          <LocationDropdownHeader
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        </Grid>

        {filteredResults?.map((object) => {
          const { serviceName, collectionName } = object;
          const collectionGeoJson = object.geoJson;

          // Find location from the collection that matches the selected location
          const collectionSelectedLocation = collectionGeoJson.features.find(
            (feature) =>
              selectedLocationNames.includes(feature.properties?.name),
          );

          return (
            <Box
              data-testid="serviceDropdown"
              key={`${collectionName}-${serviceName}`}
              sx={{ width: '100%' }}
            >
              <CustomAccordion
                amountOfItems={collectionGeoJson.features.length || 0}
                data-testid="locationDropdownCollectionAccordion"
                defaultExpanded={defaultExpanded || false}
                detailsSx={{ padding: 0 }}
                sx={{ mx: 1, mb: 1 }}
                title={collectionName}
                title2={serviceName}
              >
                <FocusableList
                  rowCount={collectionGeoJson.features.length}
                  rowHeight={40}
                  rowProps={{
                    handleChange,
                    selectedLocation:
                      collectionSelectedLocation?.properties?.name,
                    features: collectionGeoJson.features,
                    isDisabled,
                  }}
                  style={{ height: heightObserver, width: '100%' }}
                />
              </CustomAccordion>
            </Box>
          );
        })}
      </Grid>
    </Box>
  );
};
